import { Fragment, useState, useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { getAttemptReview } from "../../services/Attempts/AttemptService";
import QuestionDisplay from "../../components/ExamTaking/QuestionDisplay";
import PassagePane from "../../components/ExamTaking/PassagePane";
import { isBlankHtml } from "../../components/KaTeXComponent/RichText";
import SimpleLoader from "../../components/Loader/SimpleLoader";
import { isCanceledError } from "../../utils/examError";
import { passageRuns } from "../../utils/passages";
import { BackLink } from "./parts";

export default function ExamReview() {
  const { uuid } = useParams();
  const { t } = useTranslation();
  const location = useLocation();

  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    let isCurrent = true;

    getAttemptReview({ uuid, signal: controller.signal })
      .then((data) => {
        if (!isCurrent) return;
        setReview(data);
      })
      .catch((error) => {
        if (!isCurrent || isCanceledError(error)) {
          return;
        }
      })
      .finally(() => {
        if (!isCurrent) return;
        setLoading(false);
      });

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [uuid]);

  // Context questions: the passage's text stands before the first question of each run on it.
  const { runByIndex } = useMemo(() => passageRuns(review?.questions), [review]);
  // The user's own attempt: the server leaves out its question codes (staff see them in another's).
  const ownAttempt = useMemo(() => (review?.questions || []).every((question) => !question.question_code), [review]);

  // The review opens from the attempt's result: the way back leads there, handing back where the result was opened
  // from (My attempts), so the result's own way back still leads there.
  const backLink = (
    <BackLink to={`/exam/attempt/${uuid}/result`} state={location.state} className="mb-4">
      {t("exam_taking.back_to_result")}
    </BackLink>
  );

  if (loading) return <SimpleLoader className="h-8 w-8" />;
  if (!review) {
    return (
      <div className="mx-auto w-full max-w-4xl">
        {backLink}
        <p className="py-2 text-sm text-slate-500">{t("exam_taking.review_not_available")}</p>
      </div>
    );
  }

  const passed = review.passed;
  const score = parseFloat(review.score_percentage).toFixed(1);

  return (
    <div className="mx-auto w-full max-w-4xl motion-safe:animate-fade-in">
      {backLink}
      <div className="mb-6 flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <span
          className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${
            passed ? "bg-emerald-50" : "bg-red-50"
          }`}
        >
          {passed ? (
            <CheckCircleIcon className="h-7 w-7 text-emerald-500" />
          ) : (
            <XCircleIcon className="h-7 w-7 text-red-500" />
          )}
        </span>
        <div>
          <p className="text-lg font-semibold tabular-nums tracking-tight text-slate-900">
            {t("exam_taking.score")}: {score}%
          </p>
          <p
            className={`text-sm font-medium ${passed ? "text-emerald-600" : "text-red-600"}`}
          >
            {passed
              ? t("exam_taking.passed")
              : t("exam_taking.failed")}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        {review.questions?.map((question, index) => {
          const run = runByIndex[index];
          const passage = run?.start === index ? review.passages?.[run.passageUuid] : null;
          // A teacher's review (not an admin's): a question whose answers they may not read in the bank shows no right
          // option, and says why. What was chosen is the server's to give: in another's attempt it leaves it out (the
          // note says so); in the teacher's own attempt their pick shows, neither right nor wrong (a pick kept on such
          // a row is always the owner's).
          const hidden = question.answers_hidden === true;
          const own = ownAttempt || !!question.selected_answer_key_uuid;
          return (
            <Fragment key={question.attempt_question_uuid}>
              {passage && !isBlankHtml(passage.body) && (
                <PassagePane variant="review" run={run} passage={passage} />
              )}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("exam_taking.question")} {index + 1}
                </p>
                <QuestionDisplay
                  question={question}
                  selectedAnswerUuid={question.selected_answer_key_uuid}
                  isReview
                  correctAnswerUuids={
                    review.show_correct_answers && !hidden
                      ? question.correct_answer_key_uuids
                      : []
                  }
                  answersHiddenNote={hidden && own ? t("exam_taking.answers_hidden_own") : null}
                />
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
